import { Component, OnInit, ViewChild } from '@angular/core';
import { FinanceiroService } from '../../../../services/financeiro.service';
import { FinanceiroDashboard } from '../../../models/financeiro-dashboard';
import { BaseResponse } from '../../../../../../core/models/base-response';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

@Component({
  selector: 'app-financeiro-dashboard',
  templateUrl: './financeiro-dashboard.component.html',
  styleUrls: ['./financeiro-dashboard.component.css'],
  standalone: false
})
export class FinanceiroDashboardComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  dashboardData?: FinanceiroDashboard;

  // Gráfico Receita vs Meta
  public barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    }
  };
  public barChartType: ChartType = 'bar';
  public barChartDataReceitas: ChartData<'bar'> = {
    labels: ['Real', 'Meta'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ['#00a65a', '#92d050'],
        borderRadius: 5
      }
    ]
  };

  // Gráfico Despesa vs Meta
  public barChartDataDespesas: ChartData<'bar'> = {
    labels: ['Real', 'Meta'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ['#c00000', '#ffc000'],
        borderRadius: 5
      }
    ]
  };

  // Gráfico Rosca (Placeholder de exemplo com os dados que temos)
  public donutChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 10, font: { size: 10 } }
      }
    }
  };
  public donutChartType: ChartType = 'doughnut';
  public donutChartData: ChartData<'doughnut'> = {
    labels: ['Receitas', 'Despesas'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ['#00a65a', '#c00000']
      }
    ]
  };

  // Gráfico de Resultado
  public resultadoChartData: ChartData<'bar'> = {
    labels: ['Resultado', 'Meta'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ['#203764', '#8faadc'],
        borderRadius: 5
      }
    ]
  };

  // Gráfico de Lucratividade (Linha)
  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value + '%'
        }
      }
    },
    plugins: {
      legend: { display: false }
    }
  };
  public lineChartType: ChartType = 'line';
  public lineChartDataLucratividade: ChartData<'line'> = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
    datasets: [
      {
        data: [0, 0, 0, 0, 0, 0],
        label: 'Lucratividade',
        borderColor: '#00a65a',
        backgroundColor: 'rgba(0,166,90,0.2)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  // Gráfico Onda de Saldo
  public waveChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { display: false },
      y: { display: false }
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    },
    elements: {
      point: { radius: 0 }
    }
  };
  public waveChartDataSaldo: ChartData<'line'> = {
    labels: ['1', '2', '3', '4', '5'],
    datasets: [
      {
        data: [0, 0, 0, 0, 0],
        borderColor: '#203764',
        backgroundColor: 'rgba(32,55,100,0.2)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  // Gráfico Barras Comparativas (Contas a Receber vs Contas a Pagar)
  public barChartDataComparativo: ChartData<'bar'> = {
    labels: ['Receber', 'Pagar'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ['#92d050', '#ffc000'],
        borderRadius: 5,
        label: 'Contas'
      }
    ]
  };

  constructor(private financeiroService: FinanceiroService) {}

  ngOnInit(): void {
    this.carregarDashboard();
  }

  carregarDashboard(): void {
    this.financeiroService.obterDashboard().subscribe({
      next: (response: BaseResponse<FinanceiroDashboard>) => {
        if (response.sucesso && response.dados) {
          this.dashboardData = response.dados;
          this.atualizarGraficos();
        }
      },
      error: (err: any) => {
        console.error('Erro ao carregar dashboard financeiro', err);
      }
    });
  }

  atualizarGraficos(): void {
    if (!this.dashboardData) return;

    // Atualiza Receitas
    this.barChartDataReceitas = {
      labels: ['Real', 'Meta'],
      datasets: [
        {
          data: [this.dashboardData.realReceitas, this.dashboardData.metaReceitas],
          backgroundColor: ['#00a65a', '#92d050'],
          borderRadius: 5,
          label: 'Receitas'
        }
      ]
    };

    // Atualiza Despesas
    this.barChartDataDespesas = {
      labels: ['Real', 'Meta'],
      datasets: [
        {
          data: [this.dashboardData.realDespesas, this.dashboardData.metaDespesas],
          backgroundColor: ['#c00000', '#ffc000'],
          borderRadius: 5,
          label: 'Despesas'
        }
      ]
    };

    // Atualiza Donut
    this.donutChartData = {
      labels: ['Receitas', 'Despesas'],
      datasets: [
        {
          data: [this.dashboardData.realReceitas, this.dashboardData.realDespesas],
          backgroundColor: ['#00a65a', '#c00000'],
          label: 'Composição'
        }
      ]
    };

    // Atualiza Resultado
    this.resultadoChartData = {
      labels: ['Resultado', 'Meta'],
      datasets: [
        {
          data: [this.dashboardData.resultado, this.dashboardData.metaResultado],
          backgroundColor: ['#203764', '#8faadc'],
          borderRadius: 5,
          label: 'Resultado'
        }
      ]
    };

    // Atualiza Lucratividade
    const lucratividade = this.dashboardData.realReceitas > 0
      ? (this.dashboardData.resultado / this.dashboardData.realReceitas) * 100
      : 0;

    this.lineChartDataLucratividade = {
      labels: ['Mês Atual'],
      datasets: [
        {
          data: [lucratividade],
          label: 'Lucratividade',
          borderColor: '#00a65a',
          backgroundColor: 'rgba(0,166,90,0.2)',
          fill: true,
          tension: 0.4
        }
      ]
    };

    // Atualiza Onda de Saldo
    // Simulamos uma "onda" baseada no resultado atual para dar o efeito visual
    // No futuro, se a API retornar histórico, usaremos os dados reais
    const saldo = this.dashboardData.resultado;
    this.waveChartDataSaldo = {
      labels: ['1', '2', '3', '4', '5'],
      datasets: [
        {
          data: [saldo * 0.8, saldo * 0.9, saldo * 1.1, saldo * 0.95, saldo],
          borderColor: '#203764',
          backgroundColor: 'rgba(32,55,100,0.2)',
          fill: true,
          tension: 0.4,
          label: 'Saldo'
        }
      ]
    };

    // Atualiza Barras Comparativas
    this.barChartDataComparativo = {
      labels: ['Receber', 'Pagar'],
      datasets: [
        {
          data: [this.dashboardData.totalContasReceber, this.dashboardData.totalContasPagar],
          backgroundColor: ['#92d050', '#ffc000'],
          borderRadius: 5,
          label: 'Contas'
        }
      ]
    };

    this.chart?.update();
  }
}
